import React from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FilterButton, StyledActionButton, StyledSegmentedControl, StyledSegmentButton } from './SectionBar.styled';

interface CN54bisReportFiltersProps {
  customFilters: {
    ppo?: string;
    year?: string;
    direction?: string;
    ppoOptions?: Array<{ id: string; name: string; disabled: boolean }>;
    isPpoLoading?: boolean;
    yearOptions?: string[];
    onPpoChange?: (ppo: string) => void;
    onYearChange?: (year: string) => void;
    onDirectionChange?: (direction: string) => void;
    onExport?: (format: 'HTML' | 'PDF' | 'XLS') => void;
    isExporting?: boolean;
  };
}

const CN54bisReportFilters: React.FC<CN54bisReportFiltersProps> = ({ customFilters }) => {
  // State for dropdown menus
  const [ppoAnchorEl, setPpoAnchorEl] = React.useState<null | HTMLElement>(null);
  const [yearAnchorEl, setYearAnchorEl] = React.useState<null | HTMLElement>(null);

  const isPpoMenuOpen = Boolean(ppoAnchorEl);
  const isYearMenuOpen = Boolean(yearAnchorEl);

  // Generate year options
  const yearOptions = React.useMemo(() => {
    // If parent provides cascaded year options, use them
    if (customFilters.yearOptions && customFilters.yearOptions.length > 0) {
      return customFilters.yearOptions.map(year => ({ value: year, label: year }));
    }
    
    // Otherwise, generate all options from 2008 to current year
    const options: { value: string; label: string }[] = [];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year >= 2008; year--) {
      options.push({
        value: year.toString(),
        label: year.toString()
      });
    }
    
    return options;
  }, [customFilters.yearOptions]);

  const directionOptions = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  // Handlers
  const handlePpoClick = (event: React.MouseEvent<HTMLElement>) => {
    setPpoAnchorEl(event.currentTarget);
  };

  const handlePpoMenuClose = () => {
    setPpoAnchorEl(null);
  };

  const handlePpoChange = (ppo: string) => {
    customFilters.onPpoChange?.(ppo);
    handlePpoMenuClose();
  };

  const handleYearClick = (event: React.MouseEvent<HTMLElement>) => {
    setYearAnchorEl(event.currentTarget);
  };

  const handleYearMenuClose = () => {
    setYearAnchorEl(null);
  };

  const handleYearChange = (year: string) => {
    customFilters.onYearChange?.(year);
    handleYearMenuClose();
  };

  const handleDirectionChange = (direction: string) => {
    customFilters.onDirectionChange?.(direction);
  };

  // Display labels
  const getPpoLabel = () => {
    if (!customFilters.ppo || customFilters.ppo === '') return 'All';
    return customFilters.ppo;
  };

  const getYearLabel = () => {
    if (!customFilters.year) return 'Select Year';
    return customFilters.year;
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      {/* PPO Filter */}
      <FilterButton onClick={handlePpoClick}>
        <span className='label'>DO</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getPpoLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={ppoAnchorEl}
        open={isPpoMenuOpen}
        onClose={handlePpoMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        {customFilters.isPpoLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} color='info' />
          </Box>
        ) : (
          <>
            <MenuItem onClick={() => handlePpoChange('')} selected={!customFilters.ppo || customFilters.ppo === ''}>
              <Typography sx={{ fontSize: '14px', fontWeight: (!customFilters.ppo || customFilters.ppo === '') ? 700 : 400 }}>
                All
              </Typography>
            </MenuItem>
            {customFilters.ppoOptions?.map((option) => (
              <MenuItem
                key={option.id}
                onClick={() => handlePpoChange(option.id)}
                selected={customFilters.ppo === option.id}
                disabled={option.disabled}
                sx={{
                  fontSize: '14px',
                  fontWeight: customFilters.ppo === option.id ? 700 : 400,
                }}
              >
                {option.name}
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      {/* Direction Filter */}
      <StyledSegmentedControl>
        {directionOptions.map((option) => (
          <StyledSegmentButton
            key={option.id}
            active={customFilters.direction === option.id}
            onClick={() => handleDirectionChange(option.id)}
            sx={{ 
              color: customFilters.direction === option.id ? '#ffffff' : '#00B6BA',
              minWidth: '70px'
            }}
          >
            {option.label}
          </StyledSegmentButton>
        ))}
      </StyledSegmentedControl>

      {/* Year Filter */}
      <FilterButton onClick={handleYearClick}>
        <span className='label'>Year</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getYearLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={yearAnchorEl}
        open={isYearMenuOpen}
        onClose={handleYearMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleYearChange('')}>
          <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
        </MenuItem>
        {yearOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleYearChange(option.value)}
            selected={customFilters.year === option.value}
            sx={{
              fontWeight: customFilters.year === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Export Buttons */}
      <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }}>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('HTML')}
          disabled={customFilters.isExporting}
          variant="contained"
          size="small"
        >
          {customFilters.isExporting ? (
            <CircularProgress size={16} />
          ) : (
            'HTML'
          )}
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('PDF')}
          disabled={customFilters.isExporting}
          variant="contained"
          size="small"
        >
          {customFilters.isExporting ? (
            <CircularProgress size={16} />
          ) : (
            'PDF'
          )}
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('XLS')}
          disabled={customFilters.isExporting}
          variant="contained"
          size="small"
        >
          {customFilters.isExporting ? (
            <CircularProgress size={16} />
          ) : (
            'XLS'
          )}
        </StyledActionButton>
      </Box>
    </Box>
  );
};

export default CN54bisReportFilters;

